import { MyProjects, ProjectHub, ProjectPlan, UserNextBestActions } from '@platform/client';
import type { ProjectPlanData } from '@platform/client/schemas';
import {
  AvatarNew,
  Button,
  DatePicker,
  Icon,
  Input,
  Tabs,
  type TabItem,
} from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classix';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useApplicationLocalStorage } from '../../application/application-hook';
import { formatDateStr, localCalendarDateToApiDatetime } from '../../util/DateHelpers';
import { ProjectPlanEmptyState as EmptyState } from '../shared-components/ProjectPlanEmptyState';
import { ProjectPlanFormSection as FormSection } from '../shared-components/ProjectPlanFormSection';
import { ProjectPlanReadOnlyCard as ReadOnlyCard } from '../shared-components/ProjectPlanReadOnlyCard';
import { ExampleResponse } from './ExampleResponse';

const parsePlanDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

type DuringTheCallLocalData = {
  unavailability?: string;
  communication?: string;
  nextSteps?: string;
  deliverables?: { deliverable: string; context: string }[];
  startDate?: string;
  endDate?: string;
  taskAssignments?: Record<string, string>;
};

type EditableDeliverable = { rowId: string; deliverable: string; context: string };
type Tab = 'representative' | 'professional' | 'team';

type UserAvatarProps = { src?: string | null; name?: string };

const PlanTabUserAvatar = ({ src, name }: UserAvatarProps) =>
  name?.trim() ?
    <AvatarNew size='sm' url={src} name={name.trim()} alt={name.trim()} />
  : <AvatarNew size='sm' url={src} fallbackIcon='user-01' alt='' />;

type ContactInfo =
  { id: number; firstname: string; lastname: string; avatar?: string | null } | null | undefined;

type OrgBriefData =
  | {
      objective?: string | null;
      obstacle?: string | null;
      impact?: string | null;
      stepsTaken?: string | null;
      primaryOutcome?: string | null;
      constraints?: string | null;
      questions?: string | null;
    }
  | null
  | undefined;

type ProBriefData =
  | {
      questions?: string | null;
    }
  | null
  | undefined;

type ResponseReviewProps = {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  representative?: ContactInfo;
  professional?: ContactInfo;
  orgBrief?: OrgBriefData;
  proBrief?: ProBriefData;
  isTeam?: boolean;
  teamMembers?: { id: number; firstname: string; lastname: string; avatar?: string | null }[];
  skillAlignments?: { userId: number; skillsAlignment: string }[];
  teamQuestions?: string | null;
};

const ResponseReview = ({
  onTabChange,
  representative,
  professional,
  orgBrief,
  proBrief,
  isTeam,
  teamMembers,
  skillAlignments,
  teamQuestions,
}: ResponseReviewProps) => {
  const tabItems: TabItem[] = useMemo(() => {
    const representativeTabLabel = (
      <span className='flex items-center justify-center gap-2'>
        <PlanTabUserAvatar
          src={representative?.avatar}
          name={
            representative ?
              `${representative.firstname} ${representative.lastname}`.trim()
            : undefined
          }
        />
        <span>Representative</span>
      </span>
    );

    const representativePanel =
      orgBrief ?
        <div className='mt-4 flex flex-col gap-4'>
          {(orgBrief.objective || orgBrief.obstacle || orgBrief.impact) && (
            <ReadOnlyCard label='Challenge reframe'>
              {orgBrief.objective && (
                <p>
                  <strong>We're trying to</strong> {orgBrief.objective}
                </p>
              )}
              {orgBrief.obstacle && (
                <p className='mt-2'>
                  <strong>but we're stuck because</strong> {orgBrief.obstacle}
                </p>
              )}
              {orgBrief.impact && (
                <p className='mt-2'>
                  <strong>and it's impacting us by</strong> {orgBrief.impact}
                </p>
              )}
            </ReadOnlyCard>
          )}
          {orgBrief.stepsTaken && (
            <ReadOnlyCard label='Past attempts'>{orgBrief.stepsTaken}</ReadOnlyCard>
          )}
          {orgBrief.primaryOutcome && (
            <ReadOnlyCard label='Minimum outcome needed'>{orgBrief.primaryOutcome}</ReadOnlyCard>
          )}
          {orgBrief.constraints && (
            <ReadOnlyCard label='Constraints'>{orgBrief.constraints}</ReadOnlyCard>
          )}
          {orgBrief.questions && (
            <ReadOnlyCard label='Questions for professional'>{orgBrief.questions}</ReadOnlyCard>
          )}
        </div>
      : <EmptyState />;

    const professionalPanel = (
      <div className='mt-4 flex flex-col gap-4'>
        <ReadOnlyCard label='Skills & Experience'>
          {(() => {
            const alignment =
              (professional?.id != null ?
                skillAlignments?.find((s) => s.userId === professional.id)?.skillsAlignment
              : undefined) ?? skillAlignments?.[0]?.skillsAlignment;

            return alignment?.trim() ? alignment : <EmptyState />;
          })()}
        </ReadOnlyCard>
        {proBrief?.questions?.trim() && (
          <ReadOnlyCard label='Questions for planning call'>
            <span className='whitespace-pre-wrap'>{proBrief.questions}</span>
          </ReadOnlyCard>
        )}
      </div>
    );

    const teamPanel = (
      <div className='mt-4 flex flex-col gap-4'>
        <ReadOnlyCard label='Skills & Experience'>
          {(() => {
            const filled = (teamMembers ?? []).filter((m) =>
              skillAlignments?.find((s) => s.userId === m.id)?.skillsAlignment?.trim(),
            );
            return filled.length ?
                <div className='flex flex-col gap-3'>
                  {filled.map((m) => {
                    const alignment = skillAlignments?.find(
                      (s) => s.userId === m.id,
                    )?.skillsAlignment;
                    return (
                      <div key={m.id}>
                        <p className='font-semibold'>
                          {m.firstname} {m.lastname}:
                        </p>
                        <p>{alignment}</p>
                      </div>
                    );
                  })}
                </div>
              : <EmptyState />;
          })()}
        </ReadOnlyCard>
        {teamQuestions?.trim() && (
          <ReadOnlyCard label='Questions for planning call'>
            <span className='whitespace-pre-wrap'>{teamQuestions}</span>
          </ReadOnlyCard>
        )}
      </div>
    );

    if (isTeam) {
      return [
        {
          id: 'representative',
          tabLabel: representativeTabLabel,
          content: representativePanel,
        },
        {
          id: 'team',
          tabLabel: (
            <span className='flex items-center justify-center gap-2'>
              <AvatarNew size='sm' fallbackIcon='users-02' alt='' />
              <span>Team</span>
            </span>
          ),
          content: teamPanel,
        },
      ];
    }

    return [
      {
        id: 'representative',
        tabLabel: representativeTabLabel,
        content: representativePanel,
      },
      {
        id: 'professional',
        tabLabel: (
          <span className='flex items-center justify-center gap-2'>
            <PlanTabUserAvatar
              src={professional?.avatar}
              name={
                professional ?
                  `${professional.firstname} ${professional.lastname}`.trim()
                : undefined
              }
            />
            <span>Professional</span>
          </span>
        ),
        content: professionalPanel,
      },
    ];
  }, [
    isTeam,
    representative,
    professional,
    orgBrief,
    proBrief,
    teamMembers,
    skillAlignments,
    teamQuestions,
  ]);

  return (
    <div className='flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-4'>
      <div className='flex flex-col gap-1'>
        <span className='text-lg font-medium leading-7 text-gray-700'>Response Review</span>
        <span className='text-sm font-normal leading-5 text-gray-500'>
          Review any responses before the planning call
        </span>
      </div>

      <Tabs
        title='Response review'
        items={tabItems}
        onSelectionChange={(key) => onTabChange(key as Tab)}
        fullWidth
      />
    </div>
  );
};

type DeliverableRowProps = {
  index: number;
  control: ReturnType<typeof useForm<DuringForm>>['control'];
  onRemove: (index: number) => void;
  isCompleted: boolean;
};

const DELIVERABLE_PLACEHOLDERS = [
  {
    title:
      'Onboarding Friction Audit: A summary identifying the top 3 spots where users drop off and why.',
    context:
      'This is our top priority. Target date and additional details (e.g., Jan 15, 2024 - includes wireframes and mockups)',
  },
  {
    title: 'Simplified Journey Map: A visual outline of a new, 3-step onboarding process.',
    context:
      'This is our second most important deliverable. Target date and additional details (e.g., Jan 15, 2024 - includes wireframes and mockups)',
  },
  {
    title:
      'Quick-Win Roadmap: A prioritized list of immediate technical fixes to improve conversion.',
    context:
      'Do this one if we have extra time. Target date and additional details (e.g., Jan 15, 2024 - includes wireframes and mockups)',
  },
];

const DEFAULT_DELIVERABLE_PLACEHOLDER = {
  title: 'Deliverable title',
  context:
    'Target date and additional details (e.g., Jan 15, 2025 - includes wireframes and mockups)',
};

const DeliverableRow = ({ index, control, onRemove, isCompleted }: DeliverableRowProps) => {
  const ph = DELIVERABLE_PLACEHOLDERS[index] ?? DEFAULT_DELIVERABLE_PLACEHOLDER;
  return (
    <div className='flex items-start justify-between gap-3 sm:gap-4'>
      <div className='flex flex-1 flex-col gap-2'>
        <Controller
          name={`deliverables.${index}.deliverable`}
          control={control}
          rules={index === 0 ? { required: 'This response is required' } : undefined}
          render={({ field, fieldState }) => (
            <Input
              type='inline'
              placeholder={ph.title}
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={`deliverables.${index}.context`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              type='multiline'
              placeholder={ph.context}
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              error={fieldState.error?.message}
              characterCount={400}
            />
          )}
        />
      </div>
      {!isCompleted && (
        <Button
          variant='tertiary-danger'
          size='sm'
          onPress={() => onRemove(index)}
          additionalClassnames={`shrink-0 self-start shadow-none${index === 0 ? ' invisible' : ''}`}
          aria-hidden={index === 0}
        >
          Remove
        </Button>
      )}
    </div>
  );
};

const GuidanceTips = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <Button
        variant='link-color'
        size='md'
        leadIcon='lightbulb-02'
        leadIconClassNames='stroke-brand-500'
        tailIcon={isOpen ? 'chevron-up' : 'chevron-down'}
        tailIconClassNames='stroke-brand-500'
        onPress={() => setIsOpen((prev) => !prev)}
        additionalClassnames='h-auto min-h-0 py-1 justify-start w-fit'
      >
        Guidance & tips
      </Button>
      {isOpen && (
        <div className='mt-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-4'>
          <ul className='list-disc pl-5 flex flex-col gap-1 text-sm font-normal leading-5 text-gray-700'>
            <li>
              Choose communication channels for different needs (e.g., video for discussions, Slack
              or email for updates)
            </li>
            <li>Agree on response times so you both know what to expect</li>
            <li>Schedule frequent check-ins so you can catch issues early</li>
            <li>Decide who will schedule meetings and send reminders</li>
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

type DuringForm = {
  unavailability: string;
  communication: string;
  nextSteps: string;
  deliverables: EditableDeliverable[];
};

const INITIAL_DELIVERABLES: EditableDeliverable[] = [
  { rowId: '1', deliverable: '', context: '' },
  { rowId: '2', deliverable: '', context: '' },
  { rowId: '3', deliverable: '', context: '' },
];

const MAX_DELIVERABLES = 10;

type DuringTheCallSectionProps = {
  planId: number;
  initialData?: ProjectPlanData | null;
  isCompleted?: boolean;
  isProfessional?: boolean;
  onComplete?: () => void;
  onEdit?: () => void;
};

export const DuringTheCallSection = ({
  planId,
  initialData,
  isCompleted = false,
  isProfessional = false,
  onComplete,
  onEdit,
}: DuringTheCallSectionProps) => {
  const isTeamProp = initialData?.isTeam ?? false;
  const defaultTab: Tab =
    isProfessional ?
      isTeamProp ? 'team'
      : 'professional'
    : 'representative';
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);

  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<DuringTheCallLocalData>(`project-plan-during-${planId}`);
  const [storedData] = useState(() => getApplicationData());

  const [projectStartDate, setProjectStartDate] = useState<Date | null>(
    () =>
      parsePlanDate(initialData?.startDate ?? undefined) ??
      parsePlanDate(storedData.startDate ?? undefined),
  );
  const [projectEndDate, setProjectEndDate] = useState<Date | null>(
    () =>
      parsePlanDate(initialData?.endDate ?? undefined) ??
      parsePlanDate(storedData.endDate ?? undefined),
  );
  const [dateInlineError, setDateInlineError] = useState<string | undefined>();
  const [projectLinkError, setProjectLinkError] = useState<string | undefined>();
  const [deliverablesLimitError, setDeliverablesLimitError] = useState<string | undefined>();
  const [savedDate, setSavedDate] = useState(
    initialData?.updatedAt ? formatDateStr(new Date(initialData.updatedAt)) : '',
  );

  useEffect(() => {
    setProjectStartDate(parsePlanDate(initialData?.startDate ?? undefined));
    setProjectEndDate(parsePlanDate(initialData?.endDate ?? undefined));
  }, [initialData?.id, initialData?.startDate, initialData?.endDate]);

  const isTeam = isTeamProp;
  const teamMembers = initialData?.teamMembers ?? [];
  const [taskAssignments, setTaskAssignments] = useState<Record<number, string>>(() => {
    const fromDb = Object.fromEntries((initialData?.tasks ?? []).map((t) => [t.userId, t.task]));
    if (Object.keys(fromDb).length > 0) return fromDb;
    return Object.fromEntries(
      Object.entries(storedData.taskAssignments ?? {}).map(([k, v]) => [Number(k), v]),
    );
  });

  const { control, handleSubmit, reset, watch } = useForm<DuringForm>({
    defaultValues: {
      unavailability: initialData?.availabilityNotes || storedData.unavailability || '',
      communication: initialData?.communicationNotes || storedData.communication || '',
      nextSteps: initialData?.nextSteps || storedData.nextSteps || '',
      deliverables:
        initialData?.deliverables?.length ?
          initialData.deliverables.map((d) => ({
            rowId: String(d.id),
            deliverable: d.deliverable,
            context: d.context,
          }))
        : storedData.deliverables?.length ?
          storedData.deliverables.map((d, i) => ({
            rowId: String(i),
            deliverable: d.deliverable,
            context: d.context,
          }))
        : INITIAL_DELIVERABLES,
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      updateApplicationData({
        unavailability: values.unavailability,
        communication: values.communication,
        nextSteps: values.nextSteps,
        deliverables: values.deliverables?.map(({ deliverable, context }) => ({
          deliverable: deliverable ?? '',
          context: context ?? '',
        })),
      });
    });
    return unsubscribe;
  }, [watch, updateApplicationData]);

  const {
    fields: deliverableFields,
    append: appendDeliverable,
    remove: removeDeliverable,
  } = useFieldArray({
    control,
    name: 'deliverables',
    keyName: 'rowId',
  });

  useEffect(() => {
    if (deliverableFields.length < MAX_DELIVERABLES) setDeliverablesLimitError(undefined);
  }, [deliverableFields.length]);

  const queryClient = useQueryClient();
  const planMutation = ProjectPlan.useUpdateProjectPlan();
  const deliverablesMutation = ProjectPlan.useUpdateProjectPlanDeliverables();
  const tasksMutation = ProjectPlan.useUpdateProjectPlanTasks();
  const updateProjectDatesMutationWrapper = MyProjects.useUpdateProjectDates();
  const planUpdateMutator = ProjectHub.usePostPlanUpdate();

  const validateDatesInUi = (start: Date | null, end: Date | null): boolean => {
    if (!start || !end) {
      setDateInlineError('Please select both a start and end date');
      return false;
    }
    if (end < start) {
      setDateInlineError('End date must be after start date');
      return false;
    }
    setDateInlineError(undefined);
    return true;
  };

  const invalidateCachesAfterProjectDates = () => {
    queryClient.invalidateQueries({ queryKey: ProjectPlan.getGetProjectPlanByIdQueryKey(planId) });
    queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
    queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
    queryClient.invalidateQueries({
      queryKey: UserNextBestActions.getGetUserNextBestActionsQueryKey(),
    });
    if (initialData?.assignmentDiscoveryId != null) {
      queryClient.invalidateQueries({
        queryKey: ProjectHub.getGetProjectHubDataQueryKey({
          assignment_discovery_id: initialData.assignmentDiscoveryId,
        }),
      });
    }
    if (initialData?.projectId != null) {
      queryClient.invalidateQueries({
        queryKey: ProjectHub.getGetTeamProjectHubDataQueryKey({
          project_id: initialData.projectId,
        }),
      });
    }
  };

  const onSubmit = (data: DuringForm) => {
    setProjectLinkError(undefined);
    if (data.deliverables.length > MAX_DELIVERABLES) {
      setDeliverablesLimitError(`You can add up to ${MAX_DELIVERABLES} deliverables.`);
      return;
    }
    if (!validateDatesInUi(projectStartDate, projectEndDate)) return;
    if (!initialData?.projectId) {
      setProjectLinkError(
        'Project could not be loaded. Please refresh the page or contact support.',
      );
      return;
    }
    if (!projectStartDate || !projectEndDate) return;

    const postSubmit = () => {
      invalidateCachesAfterProjectDates();
      reset(data);
      setSavedDate(formatDateStr(new Date()));
      onComplete?.();
    };

    updateProjectDatesMutationWrapper.mutate(
      {
        data: {
          project_id: initialData.projectId,
          start_date: localCalendarDateToApiDatetime(projectStartDate),
          end_date: localCalendarDateToApiDatetime(projectEndDate),
          is_team: initialData.isTeam,
        },
      },
      {
        onError() {
          setProjectLinkError('Could not save project dates. Please try again or contact support.');
        },
        onSuccess() {
          planMutation.mutate(
            {
              planId,
              data: {
                communicationNotes: data.communication,
                availabilityNotes: data.unavailability,
                nextSteps: data.nextSteps,
              },
            },
            {
              onSuccess() {
                deliverablesMutation.mutate(
                  {
                    planId,
                    data: {
                      deliverables: data.deliverables
                        .filter(({ deliverable, context }) => deliverable.trim() || context.trim())
                        .map(({ deliverable, context }) => ({ deliverable, context })),
                    },
                  },
                  {
                    onSuccess() {
                      if (isTeam && teamMembers.length > 0) {
                        tasksMutation.mutate(
                          {
                            planId,
                            data: {
                              tasks: teamMembers.map((m) => ({
                                userId: m.id,
                                task: taskAssignments[m.id] ?? '',
                              })),
                            },
                          },
                          {
                            onSuccess() {
                              planUpdateMutator.mutate({
                                data: {
                                  outcome: 'complete',
                                  notes: null,
                                  projectId: initialData.projectId as number,
                                  isTeam,
                                },
                              });
                              postSubmit();
                            },
                          },
                        );
                      } else {
                        planUpdateMutator.mutate({
                          data: {
                            outcome: 'complete',
                            notes: null,
                            projectId: initialData.projectId as number,
                            isTeam,
                          },
                        });
                        postSubmit();
                      }
                    },
                  },
                );
              },
            },
          );
        },
      },
    );
  };

  const handleAddDeliverable = () => {
    if (deliverableFields.length >= MAX_DELIVERABLES) {
      setDeliverablesLimitError(`You can add up to ${MAX_DELIVERABLES} deliverables.`);
      return;
    }
    setDeliverablesLimitError(undefined);
    appendDeliverable({ rowId: crypto.randomUUID(), deliverable: '', context: '' });
  };

  const isSaving =
    planMutation.isPending ||
    deliverablesMutation.isPending ||
    tasksMutation.isPending ||
    updateProjectDatesMutationWrapper.isPending;
  const datesValid =
    !!projectStartDate &&
    !!projectEndDate &&
    projectEndDate >= projectStartDate &&
    !dateInlineError;
  const isSubmitDisabled = !datesValid || deliverableFields.length > MAX_DELIVERABLES || isSaving;

  return (
    <div className='flex flex-col gap-6'>
      {/* 1. Response Review with tabs */}
      <ResponseReview
        activeTab={activeTab}
        onTabChange={setActiveTab}
        representative={initialData?.representative}
        professional={initialData?.professional}
        orgBrief={initialData?.organizationBrief}
        proBrief={initialData?.proBrief}
        isTeam={isTeam}
        teamMembers={teamMembers}
        skillAlignments={initialData?.skillAlignments}
        teamQuestions={initialData?.proBrief?.questions}
      />

      {/* 2. Confirm project dates */}
      <FormSection
        title='Confirm your project dates'
        required
        description="Confirm the timeframe you're planning to work within. These dates help set expectations and can be adjusted later if needed."
      >
        {isCompleted ?
          <div className='flex flex-col gap-4 sm:flex-row'>
            <div className='flex-1'>
              <ReadOnlyCard label='Start Date'>
                {projectStartDate ? formatDateStr(projectStartDate) : '—'}
              </ReadOnlyCard>
            </div>
            <div className='flex-1'>
              <ReadOnlyCard label='End Date'>
                {projectEndDate ? formatDateStr(projectEndDate) : '—'}
              </ReadOnlyCard>
            </div>
          </div>
        : <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-4 sm:flex-row'>
              <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <DatePicker
                  label='Start date'
                  required
                  initialDate={projectStartDate ?? undefined}
                  onChange={(d) => {
                    const next = d instanceof Date ? d : new Date(d);
                    setProjectStartDate(next);
                    validateDatesInUi(next, projectEndDate);
                    updateApplicationData({ startDate: next.toISOString() });
                  }}
                  additionalClassNames='w-full max-w-full'
                  maxDate={projectEndDate ?? undefined}
                />
              </div>
              <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <DatePicker
                  label='End date'
                  required
                  initialDate={projectEndDate ?? undefined}
                  onChange={(d) => {
                    const next = d instanceof Date ? d : new Date(d);
                    setProjectEndDate(next);
                    validateDatesInUi(projectStartDate, next);
                    updateApplicationData({ endDate: next.toISOString() });
                  }}
                  additionalClassNames={cx(
                    'w-full max-w-full',
                    dateInlineError ? 'ring-error-shadow-xs' : '',
                  )}
                  minDate={projectStartDate ?? undefined}
                />
              </div>
            </div>
            {dateInlineError && <p className='text-sm text-error-500'>{dateInlineError}</p>}
            {projectLinkError && <p className='text-sm text-error-500'>{projectLinkError}</p>}
          </div>
        }

        {/* Professional availability banner */}
        <div className='flex items-start gap-4 rounded-lg border border-gray-200 bg-secondary-brand-25 px-4 py-4 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'>
          <Icon
            type='info-circle'
            size='20'
            additionalClassNames='stroke-gray-500 mt-0.5 shrink-0'
          />
          <p className='text-sm font-medium leading-5 text-gray-500'>
            <strong className='font-bold'>Professional Availability:</strong>{' '}
            {`As part of their program, this professional is available for approximately ${initialData?.timeCommitment || '40 hours per week'} to dedicate to this project.`}
          </p>
        </div>
      </FormSection>

      {/* 3. Unavailability */}
      <FormSection
        title="Add any times you won't be available"
        description="Add any times you won't be available, such as vacations, holidays, or travel."
      >
        <Controller
          name='unavailability'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              placeholder={`Mark: Dec 20-Jan 2: Holiday break\n\nSarah: March 15-20: Conference travel`}
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      {/* 4. Deliverables */}
      <FormSection
        title='Align on Deliverables'
        required
        description='Define your deliverables together. Break the project into smaller, manageable outputs that you can realistically complete within your timeline. You can edit, remove, or add items as you align on priorities.'
      >
        <div className='flex flex-col gap-4'>
          {deliverableFields.map((field, index) => (
            <DeliverableRow
              key={field.rowId}
              index={index}
              control={control}
              onRemove={removeDeliverable}
              isCompleted={isCompleted}
            />
          ))}
        </div>
        {!isCompleted && (
          <div className='flex flex-col items-start gap-2'>
            <Button
              variant='tertiary-color'
              size='sm'
              leadIcon='plus-circle'
              onPress={handleAddDeliverable}
              additionalClassnames='w-fit'
            >
              Add deliverable
            </Button>
            {deliverablesLimitError && (
              <p className='text-sm text-error-500'>{deliverablesLimitError}</p>
            )}
          </div>
        )}
      </FormSection>

      {/* 5. Initial Task Distribution (team only) */}
      {isTeam && teamMembers.length > 0 && (
        <FormSection
          title='Initial Task Distribution'
          description="Start thinking about how you'll distribute tasks across your team. This is flexible — you can update it anytime, assign multiple tasks to one person, or adjust as the project evolves."
        >
          <div className='flex flex-col gap-6'>
            {teamMembers.map((member) => (
              <div key={member.id} className='flex flex-col gap-1.5'>
                <span className='text-sm font-medium leading-5 text-gray-500'>
                  {member.firstname} {member.lastname}
                </span>
                <Input
                  type='inline'
                  placeholder='Task(s) - e.g., Research current state, Create pitch deck'
                  value={taskAssignments[member.id] ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setTaskAssignments((prev) => {
                      const next = { ...prev, [member.id]: e.target.value };
                      updateApplicationData({
                        taskAssignments: Object.fromEntries(
                          Object.entries(next).map(([k, v]) => [String(k), v]),
                        ),
                      });
                      return next;
                    });
                  }}
                  disabled={isCompleted}
                />
              </div>
            ))}
          </div>
          <ExampleResponse label='Task suggestions & examples'>
            <div className='flex flex-col gap-3'>
              <div>
                <p className='font-semibold'>Coordinate project and communication</p>
                <p>
                  Schedule meetings, manage agendas, consolidate questions, and serve as the main
                  point of contact with the organization.
                </p>
              </div>
              <div>
                <p className='font-semibold'>Market and customer research</p>
                <p>
                  Analyze target users, competitors, and positioning to clarify demand, messaging,
                  and go-to-market assumptions.
                </p>
              </div>
              <div>
                <p className='font-semibold'>Technical or systems assessment</p>
                <p>
                  Review existing tools, workflows, or technical constraints and recommend
                  improvements or requirements.
                </p>
              </div>
              <div>
                <p className='font-semibold'>Develop recommendations and materials</p>
                <p>
                  Synthesize findings into clear recommendations, frameworks, or presentation
                  materials the organization can act on.
                </p>
              </div>
            </div>
          </ExampleResponse>
        </FormSection>
      )}

      {/* 6. Communication */}
      <FormSection
        title="Agree on how you'll communicate and share feedback"
        required
        description='How often will you check in? What communication channels will you use? Who will schedule meetings?'
      >
        <Controller
          name='communication'
          control={control}
          rules={{ required: 'This response is required' }}
          render={({ field, fieldState }) => (
            <Input
              type='multiline'
              placeholder='e.g., Weekly video calls on Mondays at 2pm EST. Slack for async updates (24hr response). Feedback cycles: 48 hours for review...'
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              error={fieldState.error?.message}
              characterCount={400}
            />
          )}
        />
        <GuidanceTips />
      </FormSection>

      {/* 7. Next steps */}
      <FormSection
        title='Next Steps & Action Items'
        description="Capture the immediate next steps and who's responsible for each."
      >
        <Controller
          name='nextSteps'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              placeholder={`- Martin: Send brand guidelines by Jan 10 \n- Sarah: Share onboarding audit outline by Jan \n- Martin will email this week to set up next check-in meeting`}
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      {/* Footer */}
      <div className='flex items-center gap-4'>
        {isCompleted ?
          <>
            <Button
              variant='secondary-gray'
              size='sm'
              leadIcon='edit-04'
              leadIconClassNames='stroke-gray-500'
              onPress={onEdit}
            >
              Edit
            </Button>
            <span className='text-xs font-normal leading-[18px] text-gray-400'>
              Last saved {savedDate}
            </span>
          </>
        : <>
            <Button
              variant='primary'
              size='sm'
              leadIcon='check'
              leadIconClassNames='stroke-white'
              onPress={() => void handleSubmit(onSubmit)()}
              isDisabled={isSubmitDisabled}
            >
              {isSaving ? 'Saving...' : 'Mark complete'}
            </Button>
            <span className='text-xs font-normal leading-[18px] text-gray-400'>
              All changes saved automatically
            </span>
          </>
        }
      </div>
    </div>
  );
};
